import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from '@/contexts/AuthContext';
import { authAPI } from "@/services/api";
import SuccessModal from "@/components/modals/SuccessModal";
import LoginSuccessModal from "@/components/modals/LoginSuccessModal";
import { Eye, EyeOff, Mail, Lock, LogIn, Sparkles, AlertCircle } from "lucide-react";

interface LoginForm {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface FormErrors {
  [key: string]: string;
}

export default function Login() {
  const [form, setForm] = useState<LoginForm>({
    email: '',
    password: '',
    rememberMe: false
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { login, isAdmin } = useAuth();
  const navigate = useNavigate();

  // Validação de campos
  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'email':
        if (!value.trim()) return 'Email é obrigatório';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Email inválido';
        return '';

      case 'password':
        if (!value) return 'Senha é obrigatória';
        if (value.length < 6) return 'Senha deve ter pelo menos 6 caracteres';
        return '';

      default:
        return '';
    }
  };

  // Handle input change
  const handleInputChange = (name: string, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));

    // Validação em tempo real
    const error = validateField(name, value);
    setErrors(prev => {
      const newErrors = { ...prev, [name]: error };
      // Limpar erro geral quando usuário começa a digitar novamente
      delete newErrors.general;
      return newErrors;
    });
  };

  // Validar formulário completo0
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    const emailError = validateField('email', form.email);
    const passwordError = validateField('password', form.password);

    if (emailError) newErrors.email = emailError;
    if (passwordError) newErrors.password = passwordError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit form
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Limpar erro geral antes de tentar novamente
    setErrors(prev => {
      const { general, ...rest } = prev;
      return rest;
    });

    if (!validateForm()) {
      toast({
        title: "Dados inválidos",
        description: "Por favor, corrija os campos marcados em vermelho.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.login({
        email: form.email,
        password: form.password,
        rememberMe: form.rememberMe
      });

      if (response.success && response.data) {
        // Normalizar para garantir _id
        const normalizedUser = {
          _id: response.data.user._id || response.data.user.id,
          firstName: response.data.user.firstName,
          lastName: response.data.user.lastName,
          email: response.data.user.email,
          role: response.data.user.role,
          isEmailVerified: response.data.user.isEmailVerified
        };

        login(normalizedUser, response.data.token);
        setShowSuccessModal(true);

        setTimeout(() => {
          navigate('/');
        }, 3000);
      }

    } catch (error: unknown) {
      const errorMsg = error.message || 'Erro ao fazer login';
      setErrors(prev => ({ ...prev, general: errorMsg }));
      setErrorMessage(errorMsg);
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/15 via-accent/10 to-primary/20">
      <Helmet>
        <title>Entrar — AlphaLabs | Portal do Cliente</title>
        <meta name="description" content="Acesse sua conta AlphaLabs e gerencie seus projetos de desenvolvimento e cibersegurança." />
        <link rel="canonical" href="/login" />
      </Helmet>

      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/10 rounded-full filter blur-3xl opacity-30"></div>
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-accent/10 rounded-full filter blur-3xl opacity-30"></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md">
          <div className="bg-card/80 backdrop-blur-sm border border-border/50 shadow-2xl rounded-2xl p-8">

            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 rounded-full text-sm font-medium text-primary mb-4">
                <Sparkles className="w-4 h-4" />
                <span>Portal do Cliente</span>
              </div>

              <h1 className="text-3xl md:text-4xl font-bold mb-2">
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  Bem-vindo
                </span>
              </h1>
              <p className="text-muted-foreground">
                Acesse sua conta AlphaLabs
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-6">
              {/* General Error */}
              {errors.general && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                    <p className="text-sm text-red-700">{errors.general}</p>
                  </div>
                </div>
              )}

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary" />
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`h-11 ${errors.email ? 'border-red-500 focus:border-red-500' : 'border-border focus:border-primary'}`}
                  placeholder="seu@email.com"
                  autoComplete="email"
                />
                {errors.email && (
                  <p className="text-xs text-red-500 mt-1">{errors.email}</p>
                )}
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium flex items-center gap-2">
                  <Lock className="w-4 h-4 text-primary" />
                  Senha
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={`h-11 pr-12 ${errors.password ? 'border-red-500 focus:border-red-500' : 'border-border focus:border-primary'}`}
                    placeholder="Digite sua senha"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-xs text-red-500 mt-1">{errors.password}</p>
                )}
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="rememberMe"
                    checked={form.rememberMe}
                    onCheckedChange={(checked) =>
                      setForm(prev => ({ ...prev, rememberMe: checked as boolean }))
                    }
                  />
                  <Label htmlFor="rememberMe" className="text-sm text-muted-foreground">
                    Lembrar de mim
                  </Label>
                </div>

                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:text-primary/80 transition-colors"
                >
                  Esqueci minha senha
                </Link>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading || Object.values(errors).some(error => error)}
                className="w-full h-12 text-base font-medium bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Entrando...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <LogIn className="w-5 h-5" />
                    Entrar
                  </div>
                )}
              </Button>
            </form>

            {/* Footer */}
            <div className="text-center mt-8 pt-6 border-t border-border/50">
              <p className="text-sm text-muted-foreground">
                Ainda não tem uma conta?{" "}
                <Link
                  to="/register"
                  className="text-primary hover:text-primary/80 font-medium transition-colors"
                >
                  Criar conta gratuita
                </Link>
              </p>
            </div>

            {/* Additional Help */}
            <div className="mt-6 text-center">
              <p className="text-xs text-muted-foreground">
                Problemas para acessar?{" "}
                <Link
                  to="/contato"
                  className="text-primary hover:underline"
                >
                  Entre em contato
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
      <LoginSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        userName={form.email.split('@')[0]}
      />

      <SuccessModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Erro no Login"
        message={errorMessage}
        buttonText="Tentar Novamente"
        showEmailIcon={false}
      />
    </div>
  );
}